import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useContext, useEffect, useState } from "react";
import { SocketContext } from "../../context/socket";
import {
  setCode,
  setCurrentPage,
  setPlayerName,
  setPlayersNumber,
  setVip,
  setGameState,
} from "../../store/slices/PlayerSlice";
import propagandaPlayer from "../../assets/propaganda-player.svg";
import qr from "../../assets/qr.svg";
import { useParams } from "react-router-dom";
import "./join.css";
import { Helmet } from "react-helmet";
import { trackEvent } from "../../services/appsflyer";
import TagManager from "react-gtm-module";

export function Join() {
  const params = useParams();
  const { t } = useTranslation();
  const socket = useContext(SocketContext);
  const [roomId, setRoomId] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    trackEvent("join_page_viewed", { 'platform': 'web' })
    TagManager.dataLayer({
      dataLayer: {
        event: 'join_game_page'
      }
    })
    if (params.room) setRoomId(params.room);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const joinGame = () => {
    if (!name) {
      setError(t("join.error_name") || "name is required");
      trackEvent("room_code_entered", { 'platform': 'web', 'success': false, 'fail_reason': 'missing_name' });
    } else {
      let data: IData = {};
      data.roomId = roomId;
      data.name = name;
      data.source = "web";
      data.cuid = localStorage.getItem("CUID") || "";

      socket.emit("joinLobby", data);
    }
  };

  useEffect(() => {
    if (socket) {
      socket.off("JOIN_ERROR");
      socket.off("JOIN_SUCCESS");
      socket.off("REJOIN_SUCCESS");

      socket.on("JOIN_ERROR", (data) => {
        switch (data.type) {
          case "name":
            setError(t("errors.JOIN_ERROR_NAME") || "* Name already exists");
            trackEvent("room_code_entered", { 'platform': 'web', 'success': false, 'fail_reason': 'name_exists' });
            break;
          case "room":
            setError(t("errors.JOIN_ERROR_ROOM") || "* No such room");
            trackEvent("room_code_entered", { 'platform': 'web', 'success': false, 'fail_reason': 'invalid' });
            break;
          case "old":
            setError(t("errors.OLD_USER") || "* Old user is still connected");
            trackEvent("room_code_entered", { 'platform': 'web', 'success': false, 'fail_reason': 'old_user_still_cone' });
            break;
          case "full":
            setError(t("errors.JOIN_ERROR_ROOM_FULL") || "* Room is full");
            trackEvent("room_code_entered", { 'platform': 'web', 'success': false, 'fail_reason': 'room_full' });
            break;
          default:
            setError("");
            break;
        }
      });
      socket.on("JOIN_SUCCESS", (data) => {
        setError("");
        dispatch(setVip(data));
        dispatch(setCode(data.roomId));
        dispatch(setGameState(data.state));
        dispatch(setCurrentPage("waiting"));
      });

      socket.on("REJOIN_SUCCESS", (data) => {
        setError("");
        dispatch(setVip(data.p));
        dispatch(setCode(data.p.roomId));
        dispatch(setPlayersNumber(data.number));
        dispatch(setGameState(data.state));
        dispatch(setCurrentPage("waiting"));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  interface IData {
    name?: string;
    roomId?: string;
    source?: string;
    cuid?: string;
  }

  const handleChange = (e: any) => {
    let value = e.target.value;
    value = value.replace(/[^A-Za-z]/gi, "");
    setRoomId(value.toLocaleUpperCase());
  };

  return (
    <div
      className="player-game-min player-game-safe-y absolute inset-x-0 top-0 z-0 flex flex-col bg-join-background bg-cover brightness-110 overflow-x-hidden overflow-y-auto"
    >
      <Helmet>
        <link rel="canonical" href={`https://${process.env.REACT_APP_DOMAIN_NAME}/join`} />
        <title>Propaganda: Join game</title>
        <meta name="description" content="Joining a propaganda session page."></meta>
      </Helmet>
      <div className="relative top-0 z-30 flex shrink-0 justify-between gap-3 px-4 pt-2 desktop:mt-4 desktop:px-[15%]">
        <img
          alt="propaganda logo"
          className="h-12 w-auto max-w-[45%] object-contain xs:h-14 desktop:h-auto desktop:max-w-none"
          src={propagandaPlayer}
        ></img>
        <img
          alt="qr code"
          className="h-12 w-auto max-w-[45%] object-contain xs:h-14 desktop:h-auto desktop:max-w-none"
          src={qr}
        ></img>
      </div>
      <div className="mx-auto flex w-[90%] max-w-2xl flex-1 flex-col items-center pb-6 z-30 min-h-0">
        <h2 className="h5 text-darker-blue font-semibold xs:text-lg text-3xl desktop:text-5xl font-Inter z-30 phone:mt-4 mt-8">
          {t("join.1")}
        </h2>
        <h2 className="h5 text-darker-blue font-semibold xs:text-lg text-3xl desktop:text-5xl font-Inter z-30">
          {t("join.2")}
        </h2>
        <h2 className="h5 text-darker-blue font-semibold xs:text-lg text-3xl desktop:text-5xl font-Inter z-30">
          {t("join.3")}
        </h2>
        <input
          type="text"
          className=" w-[85%] desktop:mt-4 phone:mt-2 desktop:text-5xl desktop:w-[50%] h-16 opacity-80 rounded-lg text-dark-blue text-xl border-2 border-darker-blue z-30"
          maxLength={5}
          value={roomId}
          onChange={handleChange}
        ></input>
        <h2 className="h5 text-darker-blue font-semibold xs:text-lg desktop:text-5xl text-3xl font-Inter xs:py-2 py-4 phone:py-2 z-30">
          {t("join.name")}
        </h2>
        <input
          type="text"
          className=" w-[85%] desktop:text-5xl desktop:w-[50%] h-16 opacity-80 rounded-lg text-dark-blue text-xl border-2 border-darker-blue z-30"
          maxLength={16}
          onChange={(e) => {
            setName(e.target.value);
            dispatch(setPlayerName(e.target.value));
          }}
        ></input>
        {error && (
          <h2 className=" text-red xs:text-lg desktop:text-3xl text-2xl font-Inter font-bold py-2 z-50">
            {error}
          </h2>
        )}
        <button
          className="h-28 w-28 desktop:h-48 desktop:w-48 desktop:text-5xl desktop:mt-16 bg-btn-red text-darker-blue text-3xl font-Cairo rounded-full phone:mt-10 border-2 border-dark-blue z-30"
          onClick={() => joinGame()}
        >
          {t("join.enter")}
        </button>
      </div>
    </div>
  );
}
